import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/auth';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  useTask,
  useUpdateTask,
  useTaskComments,
  useTaskActivity,
  useCreateComment,
  useUpdateComment,
  useDeleteComment,
  useStatuses,
  useProjectTags,
  useCreateTaskLink,
  useDeleteTaskLink,
  useSearchTasks,
  useCreateTask,
  useSubscribeTask,
  useUnsubscribeTask,
  taskKeys,
} from '@/hooks/use-tasks';
import { useSearchLinkableTasks } from '@/hooks/use-all-issues';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { setTaskParent, type TaskActivity } from '@/lib/tasks';
import { sanitizeRichTextWithImages } from '@/lib/sanitize-html';
import { commentMarkdownToHtml } from '../lib/description-markdown';
import { activityVerb, timeAgo } from '../lib/activity-format';
import { computeEpicProgress } from '../lib/epic-progress';
import { openTaskSearch } from '../lib/task-nav';
import { useDebouncedValue } from '@/hooks/use-debounce';
import { cn } from '@/lib/utils';
import type { TaskPriority, TaskType, TaskStatusItem, TaskDetail, TaskComment as TaskCommentType, TaskLinkType, TaskSearchResult } from '@hygge/shared';
import {
  ArrowUpRight,
  Bell,
  BellOff,
  Calendar,
  Check,
  ChevronRight,
  CornerDownRight,
  History,
  Link2,
  Loader2,
  Maximize2,
  MessageSquare,
  Minimize2,
  MoreHorizontal,
  Pencil,
  Plus,
  Reply,
  Search,
  Send,
  Tag,
  Trash2,
  X,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { formatDistanceToNow } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { DescriptionEditor } from './description-editor';
import { TimeTrackingSection } from '@/features/time-tracker/components/time-tracking-section';
import { MentionTextarea } from './mention-textarea';
import type { MentionUser } from './mention-textarea';
import { useBoardMembers, useBoardPermission, useBoards } from '@/hooks/use-boards';
import {
  TYPE_CONFIG,
  PRIORITY_CONFIG,
  PriorityBars,
  ChipButton,
  ChipSeparator,
  AssigneeChip,
  EstimateChip,
  DueDateChip,
} from './task-config';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';

interface TaskDetailPanelProps {
  taskKey: string;
  projectId?: string;
  onClose: () => void;
}

export function TaskDetailPanel({ taskKey, projectId: projectIdProp, onClose }: TaskDetailPanelProps) {
  const { data: task, isLoading, isError } = useTask(taskKey);
  // The task on screen owns the board context, not the board the drawer was
  // opened over. A parent and a subtask can sit on different boards (see the
  // note on `openTaskSearch`), and stepping between them must not leave the
  // statuses, tags, members and "new subtask" of the previous board behind —
  // offering a status from another board is a write the API does not refuse.
  // The prop still serves the first render, before the task has arrived.
  // [HW-819]
  const projectId = task?.boardId || projectIdProp || '';
  const { data: statuses } = useStatuses(projectId);
  const updateMutation = useUpdateTask();
  const [isFullscreen, setIsFullscreen] = useState(false);
  // HW-727 (#385) is satisfied natively here: the panel is a Radix Sheet, so
  // SheetContent's onEscapeKeyDown below collapses fullscreen first and otherwise
  // lets Radix close → onOpenChange(false) → onClose. No window-level listener —
  // that would double-handle Escape alongside Radix.

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        // Hide the Sheet's built-in top-right close button ([&>button.absolute]) —
        // the header already renders its own close X that calls onClose, so this
        // avoids two close affordances.
        className={cn(
          'flex flex-col gap-0 p-0 overflow-hidden sm:max-w-none [&>button.absolute]:hidden',
          isFullscreen ? 'w-screen max-w-none border-0' : 'w-full sm:w-[600px]',
        )}
        // Escape collapses fullscreen first (preventDefault stops the Sheet from
        // closing); when not fullscreen we let Radix close → onOpenChange(false) → onClose.
        onEscapeKeyDown={(e) => {
          if (isFullscreen) {
            e.preventDefault();
            setIsFullscreen(false);
          }
        }}
      >
        <SheetTitle className="sr-only">Task details</SheetTitle>
        <SheetDescription className="sr-only">
          View and edit task {taskKey}
        </SheetDescription>
        {isError ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <p className="text-sm text-muted-foreground">Task not found or has been deleted.</p>
            <button
              type="button"
              onClick={onClose}
              className="text-xs text-primary hover:underline cursor-pointer"
            >
              Close
            </button>
          </div>
        ) : isLoading || !task ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          /* A different task gets different contents. Remounting is what stops
             one task's local state from following its key into another's: the
             comment box holds an unsent draft (`body`, CommentsSection) and the
             subtasks section a half-typed title, and both are then bound to the
             key that arrived. [HW-819] */
          <TaskDetailContent
            key={task.key}
            task={task}
            projectId={projectId}
            statuses={statuses ?? []}
            onUpdate={(field, value) =>
              updateMutation.mutate({ key: taskKey, [field]: value })
            }
            isFullscreen={isFullscreen}
            onToggleFullscreen={() => setIsFullscreen((v) => !v)}
            onClose={onClose}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

interface TaskDetailContentProps {
  task: TaskDetail;
  projectId: string;
  statuses: TaskStatusItem[];
  onUpdate: (field: string, value: unknown) => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  onClose: () => void;
}

function TaskDetailContent({
  task,
  projectId,
  statuses,
  onUpdate,
  isFullscreen,
  onToggleFullscreen,
  onClose,
}: TaskDetailContentProps) {
  const typeInfo = TYPE_CONFIG[task.type as TaskType];
  const TypeIcon = typeInfo.icon;
  // Held in a local because narrowing does not reach inside the breadcrumb's
  // `search` resolver below — a property access widens back to nullable inside
  // a closure, a `const` does not.
  const parent = task.parent;
  const [openChip, setOpenChip] = useState<string | null>(null);
  // Every editor on this screen writes the task, which this board answers for
  // with `task.manage`. One flag, because they are one question.
  const canEdit = useBoardPermission(projectId, 'task.manage');
  const { data: projectTags } = useProjectTags(projectId);
  const scrollCloseRef = useRef<(() => void) | null>(null);

  const subscribeMutation = useSubscribeTask(task.key);
  const unsubscribeMutation = useUnsubscribeTask(task.key);
  const subscribeBusy = subscribeMutation.isPending || unsubscribeMutation.isPending;

  const toggleSubscribe = () => {
    const isSubscribed = task.isSubscribed;
    const mutation = isSubscribed ? unsubscribeMutation : subscribeMutation;
    mutation.mutate(undefined, {
      onError: () =>
        toast.error(isSubscribed ? 'Failed to unfollow this task' : 'Failed to follow this task'),
    });
  };

  const toggleChip = (name: string) => {
    // Closes every chip at once: they all end in the same write.
    if (!canEdit) return;
    setOpenChip((prev) => (prev === name ? null : name));
  };

  return (
    <>
      {/* ── Header ── */}
      <div className="flex items-center gap-2.5 px-5 pt-4 pb-2 shrink-0">
        <Popover open={openChip === 'header-type'} onOpenChange={(o) => o ? toggleChip('header-type') : setOpenChip(null)}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'flex items-center justify-center h-7 w-7 rounded-md border cursor-pointer transition-colors',
                typeInfo.bg, typeInfo.border,
              )}
            >
              <TypeIcon className={cn('h-3.5 w-3.5', typeInfo.color)} />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-36 p-1" align="start">
            {(Object.entries(TYPE_CONFIG) as [TaskType, typeof typeInfo][]).map(([val, cfg]) => {
              const Icon = cfg.icon;
              return (
                <button
                  key={val}
                  type="button"
                  className={cn(
                    'flex items-center gap-2 w-full rounded-md px-2.5 py-1.5 text-xs hover:bg-accent cursor-pointer transition-colors',
                    task.type === val && 'bg-accent',
                  )}
                  onClick={() => { onUpdate('type', val); setOpenChip(null); }}
                >
                  <Icon className={cn('h-3.5 w-3.5', cfg.color)} />
                  {cfg.label}
                </button>
              );
            })}
          </PopoverContent>
        </Popover>

        <span className="text-sm font-mono font-medium text-muted-foreground">
          {task.key}
        </span>
        <span className="text-sm text-muted-foreground">
          {typeInfo.label}
        </span>

        <div className="flex-1" />
        <button
          type="button"
          title={task.isSubscribed ? 'Unfollow this task' : 'Follow this task'}
          aria-label={task.isSubscribed ? 'Unfollow this task' : 'Follow this task'}
          aria-pressed={task.isSubscribed}
          disabled={subscribeBusy}
          onClick={toggleSubscribe}
          className={cn(
            'flex items-center gap-1 h-7 rounded-full px-2 transition-colors cursor-pointer hover:bg-accent disabled:opacity-60 disabled:cursor-not-allowed',
            task.isSubscribed
              ? 'text-primary hover:text-primary'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {subscribeBusy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : task.isSubscribed ? (
            <Bell className="h-4 w-4" />
          ) : (
            <BellOff className="h-4 w-4" />
          )}
          {task.subscriberCount > 0 && (
            <span className="text-xs font-medium tabular-nums">{task.subscriberCount}</span>
          )}
        </button>
        <button
          type="button"
          title={isFullscreen ? 'Exit full screen' : 'Full screen'}
          aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
          className="flex items-center justify-center h-7 w-7 rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors cursor-pointer"
          onClick={onToggleFullscreen}
        >
          {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </button>
        <button
          type="button"
          title="Close"
          aria-label="Close"
          className="flex items-center justify-center h-7 w-7 rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors cursor-pointer"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* ── Title ── */}
      <div className="px-5 py-2 shrink-0">
        <EditableTitle
          value={task.title}
          readOnly={!canEdit}
          onSave={(title) => onUpdate('title', title)}
        />
      </div>

      {/* ── Meta Chips ── */}
      <div className="px-5 py-2.5 flex flex-wrap items-center gap-1.5 shrink-0">
        {/* Type chip */}
        <Popover open={openChip === 'type'} onOpenChange={(o) => o ? toggleChip('type') : setOpenChip(null)}>
          <PopoverTrigger asChild>
            <ChipButton className={cn(typeInfo.bg, typeInfo.border)}>
              <TypeIcon className={cn('h-3 w-3', typeInfo.color)} />
              <span className={typeInfo.color}>{typeInfo.label}</span>
            </ChipButton>
          </PopoverTrigger>
          <PopoverContent className="w-36 p-1" align="start">
            {(Object.entries(TYPE_CONFIG) as [TaskType, typeof typeInfo][]).map(([val, cfg]) => {
              const Icon = cfg.icon;
              return (
                <button
                  key={val}
                  type="button"
                  className={cn(
                    'flex items-center gap-2 w-full rounded-md px-2.5 py-1.5 text-xs hover:bg-accent cursor-pointer transition-colors',
                    task.type === val && 'bg-accent',
                  )}
                  onClick={() => { onUpdate('type', val); setOpenChip(null); }}
                >
                  <Icon className={cn('h-3.5 w-3.5', cfg.color)} />
                  {cfg.label}
                </button>
              );
            })}
          </PopoverContent>
        </Popover>

        <ChipSeparator />

        {/* Status chip */}
        <Popover open={openChip === 'status'} onOpenChange={(o) => o ? toggleChip('status') : setOpenChip(null)}>
          <PopoverTrigger asChild>
            <ChipButton>
              <div
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: statuses.find((s) => s.id === task.statusId)?.color }}
              />
              <span>{statuses.find((s) => s.id === task.statusId)?.name ?? 'Status'}</span>
            </ChipButton>
          </PopoverTrigger>
          <PopoverContent className="w-44 p-1" align="start">
            {statuses.map((s) => (
              <button
                key={s.id}
                type="button"
                className={cn(
                  'flex items-center gap-2 w-full rounded-md px-2.5 py-1.5 text-xs hover:bg-accent cursor-pointer transition-colors',
                  task.statusId === s.id && 'bg-accent',
                )}
                onClick={() => { onUpdate('statusId', s.id); setOpenChip(null); }}
              >
                <div className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                {s.name}
              </button>
            ))}
          </PopoverContent>
        </Popover>

        {/* Priority chip */}
        <Popover open={openChip === 'priority'} onOpenChange={(o) => o ? toggleChip('priority') : setOpenChip(null)}>
          <PopoverTrigger asChild>
            <ChipButton>
              <PriorityBars bars={PRIORITY_CONFIG[task.priority as TaskPriority].bars} className={PRIORITY_CONFIG[task.priority as TaskPriority].color} />
              <span>{PRIORITY_CONFIG[task.priority as TaskPriority].label}</span>
            </ChipButton>
          </PopoverTrigger>
          <PopoverContent className="w-40 p-1" align="start">
            {(Object.entries(PRIORITY_CONFIG) as [TaskPriority, typeof PRIORITY_CONFIG.none][]).map(([val, cfg]) => (
              <button
                key={val}
                type="button"
                className={cn(
                  'flex items-center gap-2 w-full rounded-md px-2.5 py-1.5 text-xs hover:bg-accent cursor-pointer transition-colors',
                  task.priority === val && 'bg-accent',
                )}
                onClick={() => { onUpdate('priority', val); setOpenChip(null); }}
              >
                <PriorityBars bars={cfg.bars} className={cfg.color} />
                {cfg.label}
              </button>
            ))}
          </PopoverContent>
        </Popover>

        {/* Assignee chip */}
        <AssigneeChip
          projectId={projectId}
          value={(task.assignees ?? []).map((a) => a.id)}
          open={openChip === 'assignee'}
          onToggle={() => toggleChip('assignee')}
          onClose={() => setOpenChip(null)}
          onChange={(ids) => onUpdate('assigneeIds', ids)}
        />

        {/* Estimate chip */}
        <EstimateChip
          projectId={task.boardId}
          value={task.storyPoints}
          open={openChip === 'estimate'}
          onToggle={() => toggleChip('estimate')}
          onClose={() => setOpenChip(null)}
          onChange={(v) => onUpdate('storyPoints', v)}
        />

        {/* Due date chip */}
        <DueDateChip
          value={task.dueDate}
          open={openChip === 'dueDate'}
          onToggle={() => toggleChip('dueDate')}
          onClose={() => setOpenChip(null)}
          onChange={(v) => onUpdate('dueDate', v)}
        />

        <ChipSeparator />

        {/* Tags chip */}
        <Popover open={openChip === 'tags'} onOpenChange={(o) => o ? toggleChip('tags') : setOpenChip(null)}>
          <PopoverTrigger asChild>
            <ChipButton dashed={!task.tags || task.tags.length === 0}>
              {task.tags && task.tags.length > 0 ? (
                <span className="flex items-center gap-1">
                  {task.tags.map((t) => (
                    <span
                      key={t.id}
                      className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium"
                      style={{ backgroundColor: `${t.color}20`, color: t.color }}
                    >
                      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: t.color }} />
                      {t.name}
                    </span>
                  ))}
                </span>
              ) : (
                <>
                  <Tag className="h-3 w-3" />
                  <span>+ Tag</span>
                </>
              )}
            </ChipButton>
          </PopoverTrigger>
          <PopoverContent className="w-52 p-1" align="start">
            {projectTags?.map((t) => {
              const active = task.tags?.some((tt) => tt.id === t.id) ?? false;
              return (
                <button
                  key={t.id}
                  type="button"
                  className={cn(
                    'flex items-center gap-2 w-full rounded-md px-2.5 py-1.5 text-xs hover:bg-accent cursor-pointer transition-colors',
                    active && 'bg-accent',
                  )}
                  onClick={() => {
                    const currentIds = task.tags?.map((tt) => tt.id) ?? [];
                    const nextIds = active
                      ? currentIds.filter((x) => x !== t.id)
                      : [...currentIds, t.id];
                    onUpdate('tagIds', nextIds);
                  }}
                >
                  <Check className={cn('h-3 w-3', active ? 'opacity-100' : 'opacity-0')} />
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: t.color }} />
                  {t.name}
                </button>
              );
            })}
            {(!projectTags || projectTags.length === 0) && (
              <p className="px-2.5 py-2 text-xs text-muted-foreground">No tags yet. Create tags in project settings.</p>
            )}
          </PopoverContent>
        </Popover>
      </div>

      {/* ── Scrollable Content ── */}
      <ScrollArea
        className="flex-1 min-h-0"
        onScroll={() => scrollCloseRef.current?.()}
      >
        <div className="pb-4">
        {/* Parent task breadcrumb */}
        {parent && (
          <div className="px-5 pt-3 pb-1 min-w-0">
            <Link
              to="/boards"
              // Types only: without `from`, `prev` widens to the union of
              // every route's search and a `view` this route does not accept
              // (`report`, from another screen) makes the spread unassignable.
              // The value handed to the resolver comes from the current
              // location either way.
              from="/boards"
              search={(prev) => openTaskSearch(prev, parent.key)}
              className="group inline-flex max-w-full items-center gap-1.5 text-xs text-muted-foreground min-w-0 transition-colors hover:text-foreground"
            >
              <ArrowUpRight className="h-3 w-3 shrink-0" />
              <span className="shrink-0">Subtask of</span>
              <span className="font-mono font-medium shrink-0 group-hover:underline">{parent.key}</span>
              <span className="truncate min-w-0" title={parent.title}>{parent.title}</span>
            </Link>
          </div>
        )}

        {/* Description */}
        <div className="px-5 py-4">
          <DescriptionEditor
            readOnly={!canEdit}
            value={task.description ?? ''}
            onChange={(desc) => onUpdate('description', desc || null)}
            placeholder="Describe the task — what needs to be done, acceptance criteria, context..."
            saveOnBlur
            taskKey={task.key}
            taskTitle={task.title}
            scrollCloseRef={scrollCloseRef}
          />
        </div>

        {/* Subtasks */}
        <div className="px-5 py-4 border-t border-border">
          <SubtasksSection task={task} projectId={projectId} />
        </div>

        {/* Linked Work Items */}
        <div className="px-5 py-4 border-t border-border">
          <LinksSection task={task} projectId={projectId} />
        </div>

        {/* Time Tracking */}
        <div className="px-5 py-4 border-t border-border">
          <TimeTrackingSection task={task} boardId={projectId} />
        </div>

        {/* Comments */}
        <div className="px-5 py-4 border-t border-border">
          <CommentsSection taskKey={task.key} boardId={projectId} />
        </div>

        {/* Activity history */}
        <div className="px-5 py-4 border-t border-border">
          <ActivitySection taskKey={task.key} />
        </div>
        </div>
      </ScrollArea>

      {/* ── Footer ── */}
      <div className="flex items-center justify-between px-5 py-3 border-t border-border shrink-0">
        <span className="text-xs text-muted-foreground font-mono">
          Esc to close
        </span>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Calendar className="h-3.5 w-3.5" />
          <span>Created {formatDistanceToNow(new Date(task.createdAt), { addSuffix: true })}</span>
        </div>
      </div>
    </>
  );
}

// ─── Link type config ─────────────────────────────────────────────────────

const LINK_TYPE_CONFIG: Record<string, { label: string; icon: string }> = {
  blocks: { label: 'Blocks', icon: '🚫' },
  is_blocked_by: { label: 'Blocked by', icon: '⛔' },
  relates_to: { label: 'Relates to', icon: '🔗' },
  duplicates: { label: 'Duplicates', icon: '📋' },
  is_duplicated_by: { label: 'Duplicated by', icon: '📋' },
};

// ─── Task Search Popover ──────────────────────────────────────────────────

function TaskSearchPopover({
  projectId,
  excludeKey,
  onSelect,
  onClose,
  placeholder = 'Search tasks...',
  crossBoard = false,
}: {
  projectId: string;
  excludeKey: string;
  onSelect: (task: TaskSearchResult) => void;
  onClose: () => void;
  placeholder?: string;
  /**
   * Search every board the caller may change tasks on, not only this one — for
   * linked work items (HW-915, HW-909). Subtasks stay on their own board.
   */
  crossBoard?: boolean;
}) {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 200);
  // Both hooks run every render; the unused one is switched off by its empty query.
  const boardSearch = useSearchTasks(projectId, crossBoard ? '' : debouncedQuery, excludeKey);
  const linkableSearch = useSearchLinkableTasks(crossBoard ? debouncedQuery : '', excludeKey);
  const { data: results, isLoading } = crossBoard ? linkableSearch : boardSearch;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className="rounded-md border border-border bg-popover shadow-md overflow-hidden animate-in fade-in-0 zoom-in-95 duration-100">
      <div className="flex items-center gap-2 px-2.5 py-2 border-b border-border">
        <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.preventDefault(); onClose(); }
          }}
        />
        <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground cursor-pointer">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="max-h-[200px] overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
        {!isLoading && results && results.length === 0 && query.length >= 1 && (
          <p className="text-xs text-muted-foreground px-3 py-3 text-center">No tasks found</p>
        )}
        {!isLoading && query.length < 1 && (
          <p className="text-xs text-muted-foreground px-3 py-3 text-center">Type to search by key or title</p>
        )}
        {results?.map((t) => (
          <button
            key={t.id}
            type="button"
            className="flex items-center gap-2.5 w-full px-3 py-2 text-left hover:bg-accent transition-colors cursor-pointer"
            onClick={() => { onSelect(t); onClose(); }}
          >
            <div className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: t.status.color }} />
            <span className="text-xs font-mono text-muted-foreground shrink-0">{t.key}</span>
            <span className="text-sm flex-1 truncate">{t.title}</span>
            {'board' in t && (t as { board?: { name: string } }).board && (
              <span className="text-[10px] text-muted-foreground shrink-0 max-w-[90px] truncate">
                {(t as { board: { name: string } }).board.name}
              </span>
            )}
            {t.assignee && (
              <Avatar className="h-4 w-4">
                <AvatarFallback className="text-[7px]">
                  {t.assignee.firstName[0]}{t.assignee.lastName[0]}
                </AvatarFallback>
              </Avatar>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Subtasks Section ─────────────────────────────────────────────────────

function SubtasksSection({ task, projectId }: { task: TaskDetail; projectId: string }) {
  // Creating a subtask and linking an existing task as one both write tasks
  // on this board, so neither is offered without `task.manage`.
  const canEdit = useBoardPermission(projectId, 'task.manage');
  const [isExpanded, setIsExpanded] = useState(task.children.length > 0);
  const [mode, setMode] = useState<'idle' | 'search' | 'create'>('idle');
  const [newTitle, setNewTitle] = useState('');
  const createMutation = useCreateTask(projectId);
  const qc = useQueryClient();
  const linkExistingMutation = useMutation({
    mutationFn: (childKey: string) => setTaskParent(childKey, task.key),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.detail(task.key) });
      qc.invalidateQueries({ queryKey: taskKeys.lists() });
    },
  });

  const handleCreateSubtask = async () => {
    const trimmed = newTitle.trim();
    if (!trimmed) return;
    await createMutation.mutateAsync({ title: trimmed, parentId: task.id });
    setNewTitle('');
    setMode('idle');
    qc.invalidateQueries({ queryKey: taskKeys.detail(task.key) });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setIsExpanded((p) => !p)}
          className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors"
        >
          <ChevronRight className={cn('h-3.5 w-3.5 transition-transform duration-150', isExpanded && 'rotate-90')} />
          Subtasks {task.children.length > 0 ? `(${task.children.length})` : ''}
        </button>
        {isExpanded && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              hidden={!canEdit}
              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors cursor-pointer px-1.5 py-0.5 rounded hover:bg-accent"
              onClick={() => setMode(mode === 'create' ? 'idle' : 'create')}
            >
              <Plus className="h-3 w-3" />
              New
            </button>
            <button
              type="button"
              hidden={!canEdit}
              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors cursor-pointer px-1.5 py-0.5 rounded hover:bg-accent"
              onClick={() => setMode(mode === 'search' ? 'idle' : 'search')}
            >
              <Search className="h-3 w-3" />
              Link existing
            </button>
          </div>
        )}
      </div>

      {isExpanded && (
        <>
          {task.children.length > 0 && (() => {
            const progress = computeEpicProgress(task.children);
            return (
              <div className="flex items-center gap-2 mb-2">
                <div className="h-1.5 flex-1 rounded-full bg-border overflow-hidden">
                  <div
                    className="h-full rounded-full bg-hygge transition-all"
                    style={{ width: `${progress.pct}%` }}
                  />
                </div>
                <span className="text-[11px] text-muted-foreground tabular-nums shrink-0">
                  {progress.done}/{progress.total} · {progress.pct}%
                </span>
              </div>
            );
          })()}

          {task.children.length > 0 && (
            <div className="space-y-1.5 mb-2">
              {task.children.map((child) => {
                const isDone = child.status.category === 'done';
                return (
                  <Link
                    key={child.id}
                    to="/boards"
                    from="/boards"
                    search={(prev) => openTaskSearch(prev, child.key)}
                    className="group flex w-full items-center gap-2.5 px-2.5 py-1.5 rounded-md border border-border hover:bg-muted/30 transition-colors"
                  >
                    <div className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: child.status.color }} />
                    <span className="text-xs font-mono text-muted-foreground shrink-0">{child.key}</span>
                    <span className={cn('text-sm flex-1 truncate', isDone && 'line-through text-muted-foreground')}>
                      {child.title}
                    </span>
                    {child.assignee && (
                      <Avatar className="h-4 w-4" aria-hidden>
                        <AvatarImage src={child.assignee.avatarUrl || undefined} />
                        <AvatarFallback className="text-[7px]">
                          {child.assignee.firstName[0]}{child.assignee.lastName[0]}
                        </AvatarFallback>
                      </Avatar>
                    )}
                  </Link>
                );
              })}
            </div>
          )}

          {mode === 'create' && (
            <div className="mb-2">
              <div className="flex items-center gap-2">
                <Input
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Subtask title..."
                  className="h-8 text-sm"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); handleCreateSubtask(); }
                    if (e.key === 'Escape') { e.preventDefault(); setMode('idle'); setNewTitle(''); }
                  }}
                />
                <Button
                  size="sm"
                  className="h-8 gap-1 shrink-0"
                  disabled={!newTitle.trim() || createMutation.isPending}
                  onClick={handleCreateSubtask}
                >
                  {createMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                  Add
                </Button>
              </div>
              <span className="text-[10px] text-muted-foreground font-mono mt-1 block">Enter to add · Esc cancel</span>
            </div>
          )}

          {mode === 'search' && (
            <div className="mb-2">
              <TaskSearchPopover
                projectId={projectId}
                excludeKey={task.key}
                placeholder="Search task to add as subtask..."
                onSelect={(result) => linkExistingMutation.mutate(result.key)}
                onClose={() => setMode('idle')}
              />
            </div>
          )}

          {task.children.length === 0 && mode === 'idle' && (
            <p className="text-xs text-muted-foreground">No subtasks yet</p>
          )}
        </>
      )}
    </div>
  );
}

// ─── Linked Work Items Section ─────────────────────────────────────────────

function LinksSection({ task, projectId }: { task: TaskDetail; projectId: string }) {
  // Linking and unlinking are task writes, checked on both boards.
  const canEdit = useBoardPermission(projectId, 'task.manage');
  // A cross-board link is removed only by somebody who may change tasks on
  // the far board too, as the server asks; otherwise its X answered 403.
  const { data: visibleBoards } = useBoards();
  const mayChangeOn = (boardId: string | undefined) =>
    !boardId ||
    boardId === projectId ||
    !!visibleBoards?.find((b) => b.id === boardId)?.currentUserPermissions?.includes('task.manage');
  const links = task.links ?? [];
  const [isExpanded, setIsExpanded] = useState(links.length > 0);
  const [isAdding, setIsAdding] = useState(false);
  const [linkType, setLinkType] = useState<TaskLinkType>('relates_to');
  const createLinkMutation = useCreateTaskLink(task.key);
  const deleteLinkMutation = useDeleteTaskLink(task.key);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setIsExpanded((p) => !p)}
          className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors"
        >
          <ChevronRight className={cn('h-3.5 w-3.5 transition-transform duration-150', isExpanded && 'rotate-90')} />
          <Link2 className="h-3.5 w-3.5" />
          Linked Work Items {links.length > 0 ? `(${links.length})` : ''}
        </button>
        {isExpanded && (
          <button
            type="button"
            hidden={!canEdit}
            className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors cursor-pointer px-1.5 py-0.5 rounded hover:bg-accent"
            onClick={() => setIsAdding((p) => !p)}
          >
            <Plus className="h-3 w-3" />
            Add linked item
          </button>
        )}
      </div>

      {isExpanded && (
        <>
          {links.length > 0 && (
            <div className="space-y-1.5 mb-2 min-w-0 overflow-hidden">
              {links.map((link) => (
                <div
                  key={link.id}
                  className="group flex items-center gap-2.5 px-2.5 py-1.5 rounded-md border border-border hover:bg-muted/30 transition-colors min-w-0"
                >
                  <span className="text-[10px] text-muted-foreground shrink-0 min-w-[70px]">
                    {LINK_TYPE_CONFIG[link.linkType]?.label ?? link.linkType}
                  </span>
                  <div className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: link.task.status.color }} />
                  <span className="text-xs font-mono text-muted-foreground shrink-0">{link.task.key}</span>
                  <span className="text-sm flex-1 min-w-0 truncate" title={link.task.title}>{link.task.title}</span>
                  {link.task.assignee && (
                    <Avatar className="h-4 w-4">
                      <AvatarImage src={link.task.assignee.avatarUrl || undefined} />
                      <AvatarFallback className="text-[7px]">
                        {link.task.assignee.firstName[0]}{link.task.assignee.lastName[0]}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <button
                    type="button"
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all cursor-pointer"
                    hidden={!canEdit || !mayChangeOn(link.task.boardId)}
                    aria-label="Remove linked work item"
                    onClick={() => deleteLinkMutation.mutate(link.id)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {isAdding && (
            <div className="mb-2 space-y-2">
              <div className="flex items-center gap-2">
                <select
                  value={linkType}
                  onChange={(e) => setLinkType(e.target.value as TaskLinkType)}
                  className="h-8 rounded-md border border-border bg-background px-2 text-xs cursor-pointer focus:outline-none"
                >
                  {Object.entries(LINK_TYPE_CONFIG).map(([val, cfg]) => (
                    <option key={val} value={val}>{cfg.label}</option>
                  ))}
                </select>
              </div>
              <TaskSearchPopover
                projectId={projectId}
                excludeKey={task.key}
                placeholder="Search tasks on any of your boards..."
                crossBoard
                onSelect={(result) => {
                  createLinkMutation.mutate({ targetKey: result.key, linkType });
                  setIsAdding(false);
                }}
                onClose={() => setIsAdding(false)}
              />
            </div>
          )}

          {links.length === 0 && !isAdding && (
            <p className="text-xs text-muted-foreground">No linked work items</p>
          )}
        </>
      )}
    </div>
  );
}

// ─── Editable Components ──────────────────────────────────────────────────

function EditableTitle({
  value,
  onSave,
  readOnly,
}: {
  value: string;
  onSave: (v: string) => void;
  readOnly?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setDraft(value); }, [value]);

  const commit = useCallback(() => {
    setIsEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) {
      onSave(trimmed);
    } else {
      setDraft(value);
    }
  }, [draft, value, onSave]);

  if (!isEditing) {
    return (
      <h2
        className={cn(
          'text-lg font-semibold text-foreground leading-tight rounded-md px-1 -mx-1 py-0.5 transition-colors',
          !readOnly && 'cursor-text hover:bg-muted/30',
        )}
        onClick={() => {
          if (readOnly) return;
          setIsEditing(true);
          requestAnimationFrame(() => inputRef.current?.focus());
        }}
      >
        {value}
      </h2>
    );
  }

  return (
    <input
      ref={inputRef}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') { e.preventDefault(); setDraft(value); setIsEditing(false); }
      }}
      className="w-full text-lg font-semibold bg-transparent text-foreground outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 px-1 -mx-1 py-0.5"
      autoFocus
    />
  );
}

// ─── Comments ─────────────────────────────────────────────────────────────

function CommentsSection({ taskKey, boardId }: { taskKey: string; boardId: string }) {
  const { data: comments, isLoading } = useTaskComments(taskKey);
  const createMutation = useCreateComment(taskKey);
  const editMutation = useUpdateComment(taskKey);
  const removeMutation = useDeleteComment(taskKey);
  const currentUserId = useAuthStore((s) => s.user?.employee?.id);
  const { data: boardMembers } = useBoardMembers(boardId);
  const [isExpanded, setIsExpanded] = useState(false);
  const [body, setBody] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  // Saying something about a task is not the same as changing it, and a board
  // may give one without the other.
  const canComment = useBoardPermission(boardId, 'comment.write');

  const mentionUsers: MentionUser[] = (boardMembers ?? []).map((m) => ({
    id: m.employeeId,
    firstName: m.firstName,
    lastName: m.lastName,
    avatarUrl: m.avatarUrl,
  }));

  const handleSubmit = async (parentId?: string) => {
    if (!body.trim()) return;
    await createMutation.mutateAsync({ body: body.trim(), parentId });
    setBody('');
    setReplyingTo(null);
  };

  const handleEdit = async (commentId: string, newBody: string) => {
    await editMutation.mutateAsync({ commentId, body: newBody });
  };

  const handleDelete = async (commentId: string) => {
    await removeMutation.mutateAsync(commentId);
  };

  const totalCount = comments
    ? comments.reduce((sum, c) => sum + 1 + (c.replies?.length ?? 0), 0)
    : 0;

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsExpanded((p) => !p)}
        className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors w-full"
      >
        <ChevronRight className={cn('h-3.5 w-3.5 transition-transform duration-150', isExpanded && 'rotate-90')} />
        <MessageSquare className="h-3.5 w-3.5" />
        Comments {totalCount > 0 ? `(${totalCount})` : ''}
      </button>

      {!isExpanded ? null : isLoading ? (
        <div className="text-xs text-muted-foreground mt-3">Loading comments...</div>
      ) : comments && comments.length > 0 ? (
        <div className="space-y-3 mt-3">
          {comments.map((comment) => (
            <div key={comment.id}>
              <CommentItem
                comment={comment}
                isOwn={comment.authorId === currentUserId}
                onReply={() => {
                  setReplyingTo(replyingTo === comment.id ? null : comment.id);
                  setBody('');
                }}
                onEdit={(newBody) => handleEdit(comment.id, newBody)}
                onDelete={() => handleDelete(comment.id)}
                isReplyActive={replyingTo === comment.id}
              />

              {comment.replies && comment.replies.length > 0 && (
                <div className="ml-8 mt-2 space-y-2 border-l-2 border-border pl-3">
                  {comment.replies.map((reply) => (
                    <CommentItem
                      key={reply.id}
                      comment={reply}
                      isReply
                      isOwn={reply.authorId === currentUserId}
                      onEdit={(newBody) => handleEdit(reply.id, newBody)}
                      onDelete={() => handleDelete(reply.id)}
                    />
                  ))}
                </div>
              )}

              {replyingTo === comment.id && (
                <div className="ml-8 mt-2 pl-3">
                  <div className="flex items-start gap-2">
                    <CornerDownRight className="h-3.5 w-3.5 text-muted-foreground mt-2.5 shrink-0" />
                    <div className="flex-1">
                      <MentionTextarea
                        value={body}
                        onChange={setBody}
                        users={mentionUsers}
                        placeholder={`Reply to ${comment.author ? comment.author.firstName : 'comment'}... (@ to mention)`}
                        className="text-sm min-h-[50px]"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                            e.preventDefault();
                            handleSubmit(comment.id);
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            setReplyingTo(null);
                            setBody('');
                          }
                        }}
                      />
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[10px] text-muted-foreground font-mono">
                          ⌘ Enter to send · Esc cancel
                        </span>
                        <div className="flex gap-1.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setReplyingTo(null); setBody(''); }}
                            className="h-7 text-xs"
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            disabled={!body.trim() || createMutation.isPending}
                            onClick={() => handleSubmit(comment.id)}
                            className="h-7 gap-1 text-xs"
                          >
                            {createMutation.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Send className="h-3 w-3" />
                            )}
                            Reply
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground mt-3">No comments yet.</p>
      )}

      {isExpanded && !replyingTo && canComment && (
        <div className="mt-3 relative">
          <MentionTextarea
            value={body}
            onChange={setBody}
            users={mentionUsers}
            placeholder="Write a comment... (@ to mention, ⌘ Enter to send)"
            className="text-sm min-h-[60px] pr-12 pb-10"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <button
            type="button"
            disabled={!body.trim() || createMutation.isPending}
            onClick={() => handleSubmit()}
            className="absolute right-2 bottom-2 flex h-8 w-8 items-center justify-center rounded-lg bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-0"
            title="Send comment"
          >
            {createMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
      )}
    </div>
  );
}

function CommentItem({
  comment,
  isOwn,
  onReply,
  onEdit,
  onDelete,
  isReply,
  isReplyActive,
}: {
  comment: TaskCommentType;
  isOwn?: boolean;
  onReply?: () => void;
  onEdit?: (body: string) => void;
  onDelete?: () => void;
  isReply?: boolean;
  isReplyActive?: boolean;
}) {
  const formatName = useEmployeeNameFormatter();
  const [isEditing, setIsEditing] = useState(false);
  const [editDraft, setEditDraft] = useState(comment.body);
  const [showMenu, setShowMenu] = useState(false);

  const commitEdit = () => {
    const trimmed = editDraft.trim();
    if (trimmed && trimmed !== comment.body) {
      onEdit?.(trimmed);
    } else {
      setEditDraft(comment.body);
    }
    setIsEditing(false);
  };

  return (
    <div className="group flex gap-2.5">
      <Avatar className={cn('mt-0.5 shrink-0', isReply ? 'h-5 w-5' : 'h-6 w-6')}>
        {comment.author ? (
          <>
            <AvatarImage src={comment.author.avatarUrl || undefined} />
            <AvatarFallback className={isReply ? 'text-[7px]' : 'text-[8px]'}>
              {comment.author.firstName[0]}{comment.author.lastName[0]}
            </AvatarFallback>
          </>
        ) : (
          <AvatarFallback className={isReply ? 'text-[7px]' : 'text-[8px]'}>SY</AvatarFallback>
        )}
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={cn('font-medium text-foreground', isReply ? 'text-[11px]' : 'text-xs')}>
            {comment.author
              ? formatName({ firstName: comment.author.firstName, lastName: comment.author.lastName })
              : 'System'}
          </span>
          <span className="text-[10px] text-muted-foreground">
            {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
          </span>
          {comment.source !== 'manual' && (
            <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">
              {comment.source}
            </Badge>
          )}

          {isOwn && !isEditing && (
            <div className="relative ml-auto">
              <button
                type="button"
                onClick={() => setShowMenu((p) => !p)}
                className="flex items-center justify-center h-5 w-5 rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
              {showMenu && (
                <div className="absolute right-0 top-6 z-10 w-28 rounded-md border border-border bg-popover shadow-md py-1 animate-in fade-in-0 zoom-in-95 duration-100">
                  <button
                    type="button"
                    className="flex items-center gap-2 w-full px-2.5 py-1.5 text-xs text-foreground hover:bg-accent transition-colors cursor-pointer"
                    onClick={() => { setIsEditing(true); setEditDraft(comment.body); setShowMenu(false); }}
                  >
                    <Pencil className="h-3 w-3" />
                    Edit
                  </button>
                  <button
                    type="button"
                    className="flex items-center gap-2 w-full px-2.5 py-1.5 text-xs text-destructive hover:bg-destructive/10 transition-colors cursor-pointer"
                    onClick={() => { onDelete?.(); setShowMenu(false); }}
                  >
                    <Trash2 className="h-3 w-3" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {isEditing ? (
          <div>
            <Textarea
              value={editDraft}
              onChange={(e) => setEditDraft(e.target.value)}
              className="text-sm min-h-[50px]"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  commitEdit();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setEditDraft(comment.body);
                  setIsEditing(false);
                }
              }}
            />
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[10px] text-muted-foreground font-mono">
                ⌘ Enter to save · Esc cancel
              </span>
              <div className="flex gap-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setEditDraft(comment.body); setIsEditing(false); }}
                  className="h-7 text-xs"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={!editDraft.trim()}
                  onClick={commitEdit}
                  className="h-7 gap-1 text-xs"
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div
            className={cn(
              'prose prose-sm dark:prose-invert max-w-none text-foreground [&_*]:my-1 [&_p]:my-1',
              isReply ? 'text-xs' : 'text-sm',
            )}
            // Imported Jira comments are markdown; rendering them raw showed
            // literal `**bold**` and `[link](url)`. Plain manual comments look
            // the same as before (breaks:true keeps their line breaks) and the
            // stored body is untouched — editing still shows the raw text.
            dangerouslySetInnerHTML={{ __html: sanitizeRichTextWithImages(commentMarkdownToHtml(comment.body)) }}
          />
        )}

        {!isEditing && onReply && (
          <button
            type="button"
            onClick={onReply}
            className={cn(
              'inline-flex items-center gap-1 mt-1 text-[11px] transition-colors cursor-pointer',
              isReplyActive
                ? 'text-hygge'
                : 'text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100',
            )}
          >
            <Reply className="h-3 w-3" />
            Reply
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Activity history ─────────────────────────────────────────────────────

function ActivityRow({ entry }: { entry: TaskActivity }) {
  const formatName = useEmployeeNameFormatter();
  const [showDiff, setShowDiff] = useState(false);
  const name = entry.actor ? formatName({ firstName: entry.actor.firstName, lastName: entry.actor.lastName }) : 'Someone';
  const initials = entry.actor
    ? `${entry.actor.firstName[0] ?? ''}${entry.actor.lastName[0] ?? ''}`.toUpperCase()
    : '?';
  const hasDiff = entry.field === 'description' && (entry.fromValue || entry.toValue);

  return (
    <li className="flex gap-2.5">
      <Avatar className="mt-0.5 h-6 w-6 shrink-0">
        {entry.actor?.avatarUrl && <AvatarImage src={entry.actor.avatarUrl} />}
        <AvatarFallback className="bg-muted text-[9px] font-medium text-muted-foreground">
          {initials}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="text-xs leading-5 text-foreground/90">
          <span className="font-medium text-foreground">{name}</span> {activityVerb(entry)}
          <span className="ml-1.5 text-[11px] text-muted-foreground">{timeAgo(entry.createdAt)}</span>
        </p>
        {hasDiff && (
          <>
            <button
              type="button"
              onClick={() => setShowDiff((v) => !v)}
              className="mt-0.5 text-[11px] font-medium text-primary hover:underline cursor-pointer"
            >
              {showDiff ? 'Hide changes' : 'Show changes'}
            </button>
            {showDiff && (
              <div className="mt-1.5 grid gap-1.5 sm:grid-cols-2">
                <div className="rounded-md border border-border bg-muted/30 p-2">
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Before
                  </div>
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none text-[11px] [&_*]:my-0.5"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(entry.fromValue || '<em>(empty)</em>'),
                    }}
                  />
                </div>
                <div className="rounded-md border border-border bg-muted/30 p-2">
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    After
                  </div>
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none text-[11px] [&_*]:my-0.5"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(entry.toValue || '<em>(empty)</em>'),
                    }}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </li>
  );
}

function ActivitySection({ taskKey }: { taskKey: string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: activity, isLoading } = useTaskActivity(isExpanded ? taskKey : undefined);

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsExpanded((p) => !p)}
        className="flex w-full items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground cursor-pointer"
      >
        <ChevronRight className={cn('h-3.5 w-3.5 transition-transform duration-150', isExpanded && 'rotate-90')} />
        <History className="h-3.5 w-3.5" />
        Activity
      </button>

      {isExpanded &&
        (isLoading ? (
          <div className="mt-3 text-xs text-muted-foreground">Loading activity…</div>
        ) : activity && activity.length > 0 ? (
          <ul className="mt-3 space-y-3">
            {activity.map((entry) => (
              <ActivityRow key={entry.id} entry={entry} />
            ))}
          </ul>
        ) : (
          <div className="mt-3 text-xs text-muted-foreground">No activity yet.</div>
        ))}
    </div>
  );
}
